import {
  BILLING_TYPE,
  HF_BILLING_CODES,
  ILR_BILLING_CODES,
  BILLING_CODES_MAPPER,
  BILLING_SCHEDULE_CODES,
  OLD_BILLING_CODES_MAPPER,
  ADDITIONAL_BILLING_CODES,
  REMOTE_DIAGNOSIS_CODES_BY_NEW_BILLING_CODES,
  REMOTE_DIAGNOSIS_CODES_BY_OLD_BILLING_CODES,
} from "static/constants";

/**
 * Resolves whether a report should follow IDTF billing (modifier codes on
 * 93297/93298, no additional 93296 code) vs INVOICE billing (no modifier,
 * 93296 required alongside 93294/93295).
 *
 * | billing_type      | patient  | Result                                                     |
 * | ----------------- | -------- | ----------------------------------------------------------- |
 * | "IDTF"            | any      | IDTF                                                        |
 * | "INVOICE"         | any      | INVOICE                                                     |
 * | "SPLIT"           | new      | IDTF                                                        |
 * | "SPLIT"           | existing | follows patient.information.idtf (if missing -> INVOICE)   |
 * | missing           | any      | IDTF (default)                                             |
 */
export const checkIsIDTFBilling = ({
  account,
  patient,
  isNewPatient = false,
} = {}) => {
  const billingType = account?.settings?.billing_type || BILLING_TYPE.IDTF;

  if (billingType === BILLING_TYPE.SPLIT) {
    if (isNewPatient) {
      return true;
    }

    return Boolean(patient?.information?.idtf);
  }

  return billingType === BILLING_TYPE.IDTF;
};

/**
 * Retrieves the remote billing diagnosis mapper based on the provided parameters.
 * @param {Date} billingDate
 * @param {Boolean} [isIDTFBilling=false]
 * @returns
 */
export const getRemoteBillingDiagnosisMapper = (isIDTFBilling = false) => {
  return isIDTFBilling
    ? REMOTE_DIAGNOSIS_CODES_BY_NEW_BILLING_CODES
    : REMOTE_DIAGNOSIS_CODES_BY_OLD_BILLING_CODES;
};

const getBillingCodeMapper = (isIDTFBilling = false) => {
  return isIDTFBilling ? BILLING_CODES_MAPPER : OLD_BILLING_CODES_MAPPER;
};

/**
 * Retrieves a mapped billing code based on the provided parameters.
 * @param {String} billingCode
 * @param {Boolean} [isIDTFBilling=false]
 * @returns
 */
export const getMappedBillingCode = ({
  billingCode,
  isIDTFBilling = false,
}) => {
  const modifiedBillingCodes = [...HF_BILLING_CODES, ...ILR_BILLING_CODES];

  if (!modifiedBillingCodes.includes(billingCode)) {
    return billingCode;
  }

  const billingCodeMapper = getBillingCodeMapper(isIDTFBilling);

  return billingCodeMapper[billingCode];
};

export const getMappedBillingCodes = ({ isIDTFBilling = false }) => {
  const billingCodeMapper = getBillingCodeMapper(isIDTFBilling);

  const billingCodes = [...new Set(Object.values(billingCodeMapper))];

  return billingCodes;
};

export const checkValidBillingCodes = (billingCodes, isIDTFBilling = false) => {
  const hasMultipleBillingCodes =
    billingCodes?.filter((code) =>
      BILLING_SCHEDULE_CODES.includes(OLD_BILLING_CODES_MAPPER[code])
    ).length > 1;

  const hasIncompleteAdditionalCodes = Object.entries(
    ADDITIONAL_BILLING_CODES
  ).some(([mainCode, additionalCodes]) => {
    const isMainCodeMissing =
      !billingCodes.some((code) =>
        Object.keys(ADDITIONAL_BILLING_CODES).includes(code)
      ) && additionalCodes.some((code) => billingCodes.includes(code));

    const isAdditionCodeMissing =
      !isIDTFBilling &&
      billingCodes.includes(mainCode) &&
      !additionalCodes.every((additionalCode) =>
        billingCodes.includes(additionalCode)
      );

    return isAdditionCodeMissing || isMainCodeMissing;
  });

  return (
    billingCodes?.length > 0 &&
    (hasMultipleBillingCodes || hasIncompleteAdditionalCodes)
  );
};

export const getMultipleBillingCodes = (codes) => {
  if (!codes) return "-";

  const billingCodes = codes?.flatMap((code) => {
    const billingCodeCount = code?.count || 1;
    return Array(billingCodeCount).fill(code.billingCode);
  });

  return billingCodes?.filter(Boolean)?.join(", ") || "-";
};

export const getUniqueCodes = (codes, key) => {
  const mappedCodes = codes
    ?.map((code) =>
      key === "billingCode" && code?.count > 1
        ? `${code?.[key]} * ${code?.count}`
        : code?.[key]
    )
    ?.filter(Boolean);

  const uniqueCodes = [...new Set(mappedCodes)];

  return uniqueCodes;
};

/**
 * Returns a list of separated codes
 *
 * @param codes {array} array of billing diagnosis codes
 * @param key {string} key to look either billing code or diagnosis code
 * @param separator {string} separator to join the values
 * @return {string} list of codes separated by separator
 */
export const getUniqueCodesWithSeparator = (codes, key, separator = ", ") => {
  const uniqueCodes = getUniqueCodes(codes, key);

  return uniqueCodes?.join(separator);
};
